<template>
  <div class="points-page">
    <section class="points-toolbar">
      <div class="points-toolbar__actions">
        <span class="points-count">共 {{ total }} 条</span>
        <button class="ghost-button" type="button" :disabled="loading" @click="reload">
          {{ loading ? "刷新中" : "刷新" }}
        </button>
      </div>
    </section>

    <section class="points-card">
      <div class="points-filters">
        <input
          v-model="filters.keyword"
          class="filter-input"
          type="text"
          placeholder="搜索用户 / 权益 / 快递单号 / 收货人"
          @keyup.enter="reload"
        />
        <select v-model="filters.category" class="filter-select" @change="reload">
          <option value="">全部类型</option>
          <option value="VIRTUAL">虚拟权益</option>
          <option value="PHYSICAL">实物周边</option>
        </select>
        <select v-model="filters.status" class="filter-select" @change="reload">
          <option value="">全部状态</option>
          <option value="UNUSED">待使用</option>
          <option value="PENDING">待发货</option>
          <option value="SHIPPED">已发货</option>
          <option value="USED">已使用</option>
          <option value="EXPIRED">已失效</option>
          <option value="CANCELLED">已取消</option>
        </select>
        <button class="primary-button" type="button" @click="reload">搜索</button>
      </div>

      <div v-if="message" class="message" :class="{ 'message--error': messageError }">{{ message }}</div>

      <div v-if="loading" class="list-state">正在加载兑换记录…</div>
      <div v-else-if="!rows.length" class="list-state">还没有兑换记录。</div>
      <table v-else class="points-table">
        <thead>
          <tr>
            <th>用户</th>
            <th>兑换权益</th>
            <th>类型</th>
            <th>积分</th>
            <th>状态</th>
            <th>快递单号</th>
            <th>收货信息</th>
            <th>兑换时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="String(row.id)">
            <td data-label="用户">
              <div class="user-name">{{ row.userNickname || "用户" }}</div>
              <div class="user-meta">UID {{ row.userId }}</div>
            </td>
            <td data-label="兑换权益">
              <span class="reward-title">{{ row.rewardTitle }}</span>
            </td>
            <td data-label="类型">
              <span class="type-pill" :class="row.category === 'PHYSICAL' ? 'type-pill--physical' : 'type-pill--virtual'">
                {{ row.categoryText }}
              </span>
            </td>
            <td data-label="积分">{{ row.pointsCost }}</td>
            <td data-label="状态">
              <span class="status-pill" :class="statusClass(row.status)">{{ row.statusText }}</span>
            </td>
            <td data-label="快递单号">
              <span v-if="row.expressNo" class="express-text">
                {{ row.expressCompany ? row.expressCompany + " " : "" }}{{ row.expressNo }}
              </span>
              <span v-else class="muted-text">-</span>
            </td>
            <td data-label="收货信息">
              <template v-if="row.category === 'PHYSICAL'">
                <div class="user-name">{{ row.receiverName || "未填写" }}</div>
                <div class="user-meta">{{ row.receiverPhone || "-" }}</div>
                <div class="address-text">{{ row.receiverAddress || "未填写收货地址" }}</div>
              </template>
              <span v-else class="muted-text">-</span>
            </td>
            <td data-label="兑换时间">{{ formatTime(row.createdAt) }}</td>
            <td class="row-actions" data-label="操作">
              <button
                v-if="row.category === 'PHYSICAL' && row.status !== 'CANCELLED'"
                class="link-button"
                type="button"
                @click="startShip(row)"
              >
                {{ row.expressNo ? "修改单号" : "填写单号" }}
              </button>
              <span v-else class="muted-text">-</span>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="total > pageSize" class="pager">
        <button class="ghost-button" type="button" :disabled="page <= 1" @click="changePage(page - 1)">上一页</button>
        <span class="pager-text">第 {{ page }} / {{ totalPages }} 页</span>
        <button class="ghost-button" type="button" :disabled="page >= totalPages" @click="changePage(page + 1)">下一页</button>
      </div>
    </section>

    <section class="points-card" v-if="shipForm.id">
      <div class="editor-head">
        <div>
          <div class="editor-eyebrow">填写快递单号</div>
          <h2>{{ shipForm.rewardTitle }}</h2>
        </div>
        <button class="link-button" type="button" @click="cancelShip">关闭</button>
      </div>

      <div class="ship-summary">
        <div class="ship-summary__line">收货人：{{ shipForm.receiverName || "未填写" }} {{ shipForm.receiverPhone || "" }}</div>
        <div class="ship-summary__line">收货地址：{{ shipForm.receiverAddress || "未填写" }}</div>
      </div>

      <div class="editor-grid">
        <label class="field">
          <span class="field-label">快递公司</span>
          <input v-model="shipForm.expressCompany" class="field-input" type="text" placeholder="例如：顺丰 / 中通" maxlength="32" />
        </label>
        <label class="field">
          <span class="field-label">快递单号</span>
          <input v-model="shipForm.expressNo" class="field-input" type="text" placeholder="填写后小程序端会展示该单号" maxlength="64" />
        </label>
      </div>

      <div class="editor-actions">
        <button class="primary-button" type="button" :disabled="saving" @click="submitShip">
          {{ saving ? "保存中…" : "保存快递单号" }}
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import { fetchPointsExchanges, shipPointsExchange } from "../api/points"

function emptyShipForm() {
  return {
    id: null,
    rewardTitle: "",
    receiverName: "",
    receiverPhone: "",
    receiverAddress: "",
    expressCompany: "",
    expressNo: ""
  }
}

export default {
  name: "PointsExchangeView",
  data() {
    return {
      loading: false,
      saving: false,
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      filters: { keyword: "", category: "", status: "" },
      shipForm: emptyShipForm(),
      message: "",
      messageError: false
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil((this.total || 0) / this.pageSize))
    }
  },
  created() {
    this.reload()
  },
  methods: {
    formatTime(value) {
      if (!value) return "-"
      return String(value).replace("T", " ").slice(0, 16)
    },
    statusClass(status) {
      if (status === "UNUSED") return "status-pill--pending"
      if (status === "PENDING") return "status-pill--todo"
      if (status === "SHIPPED") return "status-pill--shipped"
      return "status-pill--off"
    },
    setMessage(text, isError = false) {
      this.message = text
      this.messageError = isError
    },
    async reload() {
      this.loading = true
      try {
        const res = await fetchPointsExchanges({
          keyword: this.filters.keyword || undefined,
          category: this.filters.category || undefined,
          status: this.filters.status || undefined,
          page: this.page,
          pageSize: this.pageSize
        })
        const data = res.data || {}
        this.rows = data.records || []
        this.total = Number(data.total || 0)
      } catch (error) {
        this.setMessage((error && error.message) || "兑换记录加载失败", true)
      } finally {
        this.loading = false
      }
    },
    changePage(next) {
      this.page = Math.min(Math.max(1, next), this.totalPages)
      this.reload()
    },
    startShip(row) {
      this.message = ""
      this.messageError = false
      this.shipForm = {
        id: row.id,
        rewardTitle: row.rewardTitle,
        receiverName: row.receiverName || "",
        receiverPhone: row.receiverPhone || "",
        receiverAddress: row.receiverAddress || "",
        expressCompany: row.expressCompany || "",
        expressNo: row.expressNo || ""
      }
    },
    cancelShip() {
      this.shipForm = emptyShipForm()
      this.saving = false
    },
    async submitShip() {
      if (!this.shipForm.id) return
      const expressNo = (this.shipForm.expressNo || "").trim()
      if (!expressNo) {
        this.setMessage("请填写快递单号", true)
        return
      }
      this.saving = true
      try {
        await shipPointsExchange(this.shipForm.id, {
          expressCompany: (this.shipForm.expressCompany || "").trim(),
          expressNo
        })
        this.setMessage("快递单号已保存")
        this.shipForm = emptyShipForm()
        await this.reload()
      } catch (error) {
        this.setMessage((error && error.message) || "快递单号保存失败", true)
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.points-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.points-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 18px;
  border-radius: 14px;
  background: #ffffff;
}

.points-toolbar__title {
  font-size: 18px;
  font-weight: 700;
  color: #24211d;
}

.points-toolbar__desc {
  margin-top: 6px;
  max-width: 720px;
  font-size: 13px;
  line-height: 20px;
  color: #7c746b;
}

.points-toolbar__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  gap: 10px;
}

.points-count {
  font-size: 13px;
  color: #7c746b;
}

.points-card {
  padding: 18px 20px;
  border-radius: 14px;
  background: #ffffff;
}

.points-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}

.filter-input,
.filter-select {
  height: 36px;
  padding: 0 12px;
  border: 1px solid #e4ded6;
  border-radius: 8px;
  background: #fbfaf8;
  font-size: 13px;
  color: #2c2824;
}

.filter-input {
  min-width: 260px;
  flex: 1;
}

.primary-button,
.ghost-button {
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  border: none;
  font-size: 13px;
  cursor: pointer;
}

.primary-button {
  background: #1f1d1a;
  color: #ffffff;
}

.ghost-button {
  background: #f2efe9;
  color: #4a443c;
}

.message {
  margin-bottom: 12px;
  font-size: 13px;
  color: #3f7a3a;
}

.message--error {
  color: #b5483f;
}

.list-state {
  padding: 24px 0;
  text-align: center;
  font-size: 13px;
  color: #9a938a;
}

.points-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: #2c2824;
}

.points-table th {
  padding: 10px 8px;
  border-bottom: 1px solid #eee9e2;
  text-align: left;
  font-weight: 600;
  color: #8b837a;
  white-space: nowrap;
}

.points-table td {
  padding: 12px 8px;
  border-bottom: 1px solid #f4f1ec;
  vertical-align: top;
}

.user-name {
  font-weight: 600;
}

.user-meta {
  margin-top: 3px;
  font-size: 12px;
  color: #9a938a;
}

.address-text {
  margin-top: 3px;
  max-width: 220px;
  font-size: 12px;
  line-height: 18px;
  color: #7c746b;
}

.reward-title {
  font-weight: 600;
}

.express-text {
  font-family: Menlo, Consolas, monospace;
}

.muted-text {
  color: #b3aca3;
}

.type-pill,
.status-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  white-space: nowrap;
}

.type-pill--virtual {
  background: #eef4ea;
  color: #4b7a41;
}

.type-pill--physical {
  background: #f3eee6;
  color: #8a6a3a;
}

.status-pill--pending {
  background: #fbf1d2;
  color: #8a6a1c;
}

.status-pill--todo {
  background: #f0e6f7;
  color: #6b4a86;
}

.status-pill--shipped {
  background: #e6f0f7;
  color: #3d6b8a;
}

.status-pill--off {
  background: #f1f0ee;
  color: #9a938a;
}

.row-actions {
  white-space: nowrap;
}

.link-button {
  padding: 0;
  border: none;
  background: none;
  color: #3f6ea8;
  font-size: 13px;
  cursor: pointer;
}

.pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
}

.pager-text {
  font-size: 13px;
  color: #7c746b;
}

.editor-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;
}

.editor-eyebrow {
  font-size: 12px;
  color: #9a938a;
}

.editor-head h2 {
  margin: 4px 0 0;
  font-size: 17px;
  color: #24211d;
}

.ship-summary {
  padding: 12px 14px;
  border-radius: 10px;
  background: #faf8f4;
  font-size: 13px;
  line-height: 20px;
  color: #5c554c;
}

.ship-summary__line + .ship-summary__line {
  margin-top: 4px;
}

.editor-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-size: 12px;
  color: #8b837a;
}

.field-input {
  height: 38px;
  padding: 0 12px;
  border: 1px solid #e4ded6;
  border-radius: 8px;
  background: #fbfaf8;
  font-size: 13px;
  color: #2c2824;
}

.editor-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
</style>
